import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Zap, 
  Mic, 
  MicOff, 
  Camera, 
  Upload, 
  Search,
  ArrowLeft,
  Loader2,
  ChevronRight,
  Globe,
  Package,
  X,
  CheckCircle,
  AlertCircle,
  FileText,
  History
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { apiPost, ApiError } from '../lib/apiClient';

const MobileAnalyzer = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activeMode, setActiveMode] = useState('text'); // text, voice, camera, upload
  const [description, setDescription] = useState('');
  const [countryOfOrigin, setCountryOfOrigin] = useState('China');
  const [importCountry, setImportCountry] = useState('United States');
  const [currentHsCode, setCurrentHsCode] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Camera state
  const [cameraActive, setCameraActive] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [processingImage, setProcessingImage] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  // File upload state
  const fileInputRef = useRef(null);
  const [uploadedFile, setUploadedFile] = useState(null);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('decoded_token')}`
  });

  // Voice Recording Functions
  const startRecording = async () => {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Microphone not supported on this device');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Check for supported MIME types
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
          mimeType = 'audio/ogg';
        }
      }

      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info('Recording... Tap again to stop');
    } catch (error) {
      console.error('Microphone error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Microphone permission denied. Please allow microphone access.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No microphone found on this device');
      } else {
        toast.error('Could not access microphone: ' + error.message);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (blob) => {
    setTranscribing(true);
    const formData = new FormData();
    // Use appropriate file extension based on MIME type
    const ext = blob.type.includes('mp4') ? 'mp4' : blob.type.includes('ogg') ? 'ogg' : 'webm';
    // Backend expects 'file' parameter, not 'audio'
    formData.append('file', blob, `recording.${ext}`);

    try {
      const data = await apiPost('/api/voice/transcribe', formData);
      // Handle the nested response structure from backend
      const extracted = data.extracted_data || {};
      const description = extracted.description || data.raw_transcription || data.text || '';
      setDescription(description);

      // Set country of origin if detected
      if (extracted.country_of_origin) {
        setCountryOfOrigin(extracted.country_of_origin);
      }

      toast.success('Voice transcribed!');
      setActiveMode('text');
    } catch (error) {
      console.error('Transcription error:', error);
      if (error instanceof ApiError) {
        toast.error(error.message || 'Transcription failed');
      } else {
        toast.error('Connection error');
      }
    } finally {
      setTranscribing(false);
    }
  };

  // Camera Functions
  const startCamera = async () => {
    // Prevent double activation
    if (cameraActive || streamRef.current) {
      console.log('Camera already active');
      return;
    }

    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Camera not supported on this device');
        return;
      }

      // Request camera permission with fallback
      let stream;
      try {
        // Try rear camera first
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          } 
        });
      } catch (err) {
        // Fallback to any available camera
        console.log('Rear camera not available, trying any camera');
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: true 
        });
      }

      streamRef.current = stream;
      
      // Set camera active first so the video element renders
      setCameraActive(true);
      
      // Wait for next tick to ensure video element is in DOM
      setTimeout(() => {
        if (videoRef.current && streamRef.current) {
          videoRef.current.srcObject = streamRef.current;
          videoRef.current.play().catch(e => {
            console.log('Autoplay failed:', e);
          });
        }
      }, 100);
      
      toast.success('Camera activated');
    } catch (error) {
      console.error('Camera error:', error);
      streamRef.current = null;
      setCameraActive(false);
      
      if (error.name === 'NotAllowedError') {
        toast.error('Camera permission denied. Please allow camera access in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No camera found on this device');
      } else if (error.name === 'NotReadableError') {
        toast.error('Camera is in use by another application');
      } else {
        toast.error('Could not access camera: ' + error.message);
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const capturePhoto = () => {
    console.log('Capture photo called');
    
    if (!videoRef.current || !canvasRef.current) {
      console.error('Video or canvas ref not available');
      toast.error('Camera not ready. Please try again.');
      return;
    }
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Check if video has valid dimensions
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      console.error('Video dimensions are 0');
      toast.error('Camera not ready. Please wait a moment and try again.');
      return;
    }
    
    console.log('Video dimensions:', video.videoWidth, 'x', video.videoHeight);
    
    // Set canvas dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw the video frame to canvas
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Convert to blob and analyze
    canvas.toBlob(async (blob) => {
      if (!blob) {
        console.error('Failed to create blob from canvas');
        toast.error('Failed to capture image. Please try again.');
        return;
      }
      
      console.log('Blob created, size:', blob.size);
      setCapturedImage(URL.createObjectURL(blob));
      stopCamera();
      toast.info('Analyzing image...');
      await analyzeImage(blob);
    }, 'image/jpeg', 0.8);
  };

  const analyzeImage = async (blob) => {
    setProcessingImage(true);
    const formData = new FormData();
    // Backend expects 'file' parameter, not 'image'
    formData.append('file', blob, 'capture.jpg');

    try {
      const data = await apiPost('/api/image/analyze', formData);
      // Handle the nested response structure from backend
      const extracted = data.extracted_data || {};
      const description = extracted.description || extracted.extracted_text || '';
      setDescription(description);

      // Set country of origin if detected
      if (extracted.country_of_origin) {
        setCountryOfOrigin(extracted.country_of_origin);
      }

      toast.success('Image analyzed!');
      setActiveMode('text');
    } catch (error) {
      console.error('Image analysis error:', error);
      if (error instanceof ApiError) {
        toast.error(error.message || 'Image analysis failed');
      } else {
        toast.error('Connection error');
      }
    } finally {
      setProcessingImage(false);
      setCapturedImage(null);
    }
  };

  // File Upload Functions
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(csv|xlsx|xls)$/)) {
      toast.error('Please upload CSV or Excel file');
      return;
    }

    setUploadedFile(file);
    navigate('/analyzer?tab=bulk', { state: { file } });
  };

  // Analyze Product
  const analyzeProduct = async () => {
    if (!description.trim()) {
      toast.error('Please enter product description');
      return;
    }

    setAnalyzing(true);
    try {
      const data = await apiPost('/api/analyze/single', {
        description,
        country_of_origin: countryOfOrigin,
        import_country: importCountry,
        current_hs_code: currentHsCode || null,
      });
      setResult(data);
      toast.success('Analysis complete!');
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(error.message || 'Analysis failed');
      } else {
        toast.error('Connection error');
      }
    } finally {
      setAnalyzing(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Connect video stream when camera becomes active
  useEffect(() => {
    if (cameraActive && videoRef.current && streamRef.current) {
      console.log('Connecting video stream to element');
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(e => console.log('Play error in useEffect:', e));
    }
  }, [cameraActive]);

  // Mode buttons
  const modes = [
    { id: 'text', icon: Search, label: 'Type' },
    { id: 'voice', icon: Mic, label: 'Voice' },
    { id: 'camera', icon: Camera, label: 'Scan' },
    { id: 'upload', icon: Upload, label: 'Bulk' }
  ];

  return (
    <div className="min-h-screen bg-[#F7F9FC] pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0B3D91] to-[#1E5EFF] text-white px-4 pt-12 pb-6 safe-area-top">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigate('/dashboard')} className="p-2 -ml-2 rounded-lg hover:bg-white/10">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <button onClick={() => navigate('/history')} className="p-2 -mr-2 rounded-lg hover:bg-white/10">
            <History className="w-6 h-6" />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
            <Zap className="w-7 h-7" />
          </div>
          <div>
            <h1 className="font-outfit text-2xl font-bold">Decoded</h1>
            <p className="text-white/80 text-sm">Quick Classification</p>
          </div>
        </div>
      </div>

      {/* Mode Selector */}
      <div className="px-4 -mt-4">
        <div className="bg-white rounded-2xl shadow-lg p-2 flex gap-2">
          {modes.map((mode) => (
            <button
              key={mode.id}
              onClick={() => setActiveMode(mode.id)}
              className={`flex-1 flex flex-col items-center gap-1 py-3 rounded-xl transition-all ${
                activeMode === mode.id 
                  ? 'bg-[#0B3D91] text-white' 
                  : 'text-slate-500 hover:bg-slate-100'
              }`}
              data-testid={`mode-${mode.id}`}
            >
              <mode.icon className="w-5 h-5" />
              <span className="text-xs font-medium">{mode.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-6">
        {/* Text Input Mode */}
        {activeMode === 'text' && !result && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Product Description
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your product in detail..."
                className="min-h-[120px] text-base"
                data-testid="product-description"
              />
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-sm space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Globe className="w-4 h-4 inline mr-1" /> Country of Origin
                </label>
                <Input
                  value={countryOfOrigin}
                  onChange={(e) => setCountryOfOrigin(e.target.value)}
                  placeholder="e.g., China"
                  data-testid="country-origin"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Package className="w-4 h-4 inline mr-1" /> Import Country
                </label>
                <Input
                  value={importCountry}
                  onChange={(e) => setImportCountry(e.target.value)}
                  placeholder="e.g., United States"
                  data-testid="import-country"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Current HS Code (Optional)
                </label>
                <Input
                  value={currentHsCode}
                  onChange={(e) => setCurrentHsCode(e.target.value)}
                  placeholder="e.g., 8471.30"
                  data-testid="current-hs"
                />
              </div>
            </div>

            <Button
              onClick={analyzeProduct}
              disabled={analyzing || !description.trim()}
              className="w-full py-6 text-lg bg-gradient-to-r from-[#0B3D91] to-[#1E5EFF] rounded-xl"
              data-testid="analyze-btn"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5 mr-2" />
                  Analyze Product
                </>
              )}
            </Button>
          </div>
        )}

        {/* Voice Mode */}
        {activeMode === 'voice' && (
          <div className="flex flex-col items-center py-12">
            <div 
              className={`w-32 h-32 rounded-full flex items-center justify-center mb-6 transition-all ${
                isRecording 
                  ? 'bg-red-500 animate-pulse' 
                  : transcribing 
                    ? 'bg-amber-500'
                    : 'bg-gradient-to-br from-[#0B3D91] to-[#1E5EFF]'
              }`}
            >
              {transcribing ? (
                <Loader2 className="w-12 h-12 text-white animate-spin" />
              ) : isRecording ? (
                <MicOff className="w-12 h-12 text-white" />
              ) : (
                <Mic className="w-12 h-12 text-white" />
              )}
            </div>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              {transcribing ? 'Transcribing...' : isRecording ? 'Recording...' : 'Tap to Record'}
            </h3>
            <p className="text-slate-500 text-center mb-8">
              {isRecording 
                ? 'Tap the button again to stop recording'
                : 'Describe your product using voice'}
            </p>

            <Button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={transcribing}
              className={`px-8 py-6 text-lg rounded-xl ${
                isRecording 
                  ? 'bg-red-500 hover:bg-red-600' 
                  : 'bg-gradient-to-r from-[#0B3D91] to-[#1E5EFF]'
              }`}
              data-testid="voice-btn"
            >
              {isRecording ? 'Stop Recording' : 'Start Recording'}
            </Button>
          </div>
        )}

        {/* Camera Mode */}
        {activeMode === 'camera' && (
          <div className="space-y-4">
            {!cameraActive && !capturedImage && (
              <div className="flex flex-col items-center py-12">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#0B3D91] to-[#1E5EFF] flex items-center justify-center mb-6">
                  <Camera className="w-12 h-12 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Scan Product</h3>
                <p className="text-slate-500 text-center mb-8">
                  Take a photo of your product for AI analysis
                </p>
                <Button
                  onClick={startCamera}
                  className="px-8 py-6 text-lg bg-gradient-to-r from-[#0B3D91] to-[#1E5EFF] rounded-xl"
                  data-testid="start-camera-btn"
                >
                  Open Camera
                </Button>
              </div>
            )}

            {cameraActive && (
              <div className="fixed inset-0 bg-black z-50">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  onLoadedMetadata={() => {
                    console.log('Video metadata loaded');
                    if (videoRef.current) {
                      videoRef.current.play().catch(e => console.log('Video play error:', e));
                    }
                  }}
                  onPlay={() => console.log('Video started playing')}
                  onError={(e) => console.error('Video error:', e)}
                />
                <canvas ref={canvasRef} className="hidden" />
                
                {/* Camera Controls - Fixed at bottom */}
                <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-6 px-4 safe-area-bottom">
                  <button
                    onClick={stopCamera}
                    className="flex items-center justify-center bg-white/95 backdrop-blur rounded-full px-8 py-4 text-base font-semibold text-slate-700 shadow-xl active:scale-95 transition-transform"
                  >
                    <X className="w-6 h-6 mr-2" /> Cancel
                  </button>
                  <button
                    onClick={capturePhoto}
                    className="flex items-center justify-center bg-white rounded-full px-8 py-4 text-base font-semibold text-[#0B3D91] shadow-xl active:scale-95 transition-transform"
                    data-testid="capture-btn"
                  >
                    <Camera className="w-6 h-6 mr-2" /> Capture
                  </button>
                </div>
                
                {/* Viewfinder overlay */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 border-2 border-white/50 rounded-3xl" />
                </div>
                
                {/* Top instruction */}
                <div className="absolute top-12 left-0 right-0 text-center safe-area-top">
                  <p className="text-white/90 text-lg font-medium">Point camera at product</p>
                </div>
              </div>
            )}

            {processingImage && (
              <div className="flex flex-col items-center py-12">
                <Loader2 className="w-16 h-16 text-[#0B3D91] animate-spin mb-4" />
                <p className="text-slate-600">Analyzing image...</p>
              </div>
            )}
          </div>
        )}

        {/* Upload Mode */}
        {activeMode === 'upload' && (
          <div className="flex flex-col items-center py-12">
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#0B3D91] to-[#1E5EFF] flex items-center justify-center mb-6">
              <Upload className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Bulk Upload</h3>
            <p className="text-slate-500 text-center mb-8">
              Upload CSV or Excel file for batch analysis
            </p>
            
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
            />
            
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="px-8 py-6 text-lg bg-gradient-to-r from-[#0B3D91] to-[#1E5EFF] rounded-xl"
              data-testid="upload-btn"
            >
              <FileText className="w-5 h-5 mr-2" />
              Select File
            </Button>
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg text-slate-900">Classification Result</h3>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  (result.result?.confidence_score || 0) * 100 >= 80 
                    ? 'bg-green-100 text-green-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {Math.round((result.result?.confidence_score || 0) * 100)}% confidence
                </span>
              </div>

              <div className="space-y-4">
                <div className="bg-gradient-to-r from-[#0B3D91]/5 to-[#1E5EFF]/5 rounded-xl p-4">
                  <p className="text-sm text-slate-500 mb-1">Suggested HS Code</p>
                  <p className="text-2xl font-bold text-[#0B3D91] font-mono">
                    {result.result?.suggested_hs_code || 'N/A'}
                  </p>
                  <p className="text-sm text-slate-600 mt-1">
                    {result.result?.description || ''}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-500">Suggested Rate</p>
                    <p className="text-lg font-bold text-slate-900">
                      {result.result?.tariff_suggested || 0}%
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-500">Current Rate</p>
                    <p className="text-lg font-bold text-slate-900">
                      {result.result?.tariff_current || 0}%
                    </p>
                  </div>
                </div>

                {result.result?.reasoning && (
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-sm text-slate-500 mb-2">AI Reasoning</p>
                    <p className="text-sm text-slate-700">
                      {result.result.reasoning}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setResult(null);
                  setDescription('');
                }}
                variant="outline"
                className="flex-1 py-5 rounded-xl"
              >
                New Analysis
              </Button>
              <Button
                onClick={() => navigate(`/analysis/${result.analysis_id}`)}
                className="flex-1 py-5 bg-gradient-to-r from-[#0B3D91] to-[#1E5EFF] rounded-xl"
              >
                View Details
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 safe-area-bottom">
        <div className="flex justify-around">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex flex-col items-center gap-1 text-slate-400"
          >
            <Zap className="w-6 h-6" />
            <span className="text-xs">Home</span>
          </button>
          <button 
            className="flex flex-col items-center gap-1 text-[#0B3D91]"
          >
            <Search className="w-6 h-6" />
            <span className="text-xs font-medium">Analyze</span>
          </button>
          <button 
            onClick={() => navigate('/history')}
            className="flex flex-col items-center gap-1 text-slate-400"
          >
            <History className="w-6 h-6" />
            <span className="text-xs">History</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileAnalyzer;
